import useSWRImmutable from "swr/immutable";
import { axios, isAxiosError } from "@root/libs/axios";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const newrelic: any;

// Ajax 系エンドポイントの status:false 応答（rescue 由来のエラーレスポンス）を表すエラー名。
// サーバ側で決定的に失敗し続けるため、この名前のエラーのみリトライを止める
const AJAX_ENVELOPE_ERROR_NAME = "AjaxEnvelopeError";

// サーバ側で決定的に失敗し続けるため再試行しても結果が変わらないエラーかどうか。
// - status:false のエンベロープ応答（rescue 由来）
// - HTTP 4xx（before_action 由来の 401 / 404 / 410 等。リクエスト内容が同じなら同じ結果になる）
const isDeterministicError = (err: Error): boolean => {
  if (err.name === AJAX_ENVELOPE_ERROR_NAME) return true;

  const status = isAxiosError(err) ? err.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
};

// GETのクエリストリング長制限を回避するため、読み取り系リクエストをPOSTで行うフック（再検証なし）。
// SWRはキーの`stableHash`でコンテンツベースにキャッシュするため、bodyの参照が毎回変わっても
// 内容が同じなら同一キャッシュが使われる。
// NOTE: 配列キーのfetcherへの渡し方はSWR 1.xが引数展開・2.xが単一引数と異なるため、
// fetcherはキー引数を受けずクロージャでurl/bodyを参照する（両バージョン対応）。
export const useSWRPostData = <T>(url: string, body: object): T | null => {
  const { data, error } = useSWRImmutable<T>(
    [url, body],
    () =>
      axios.post<T>(url, body).then((res) => {
        // Ajax 系エンドポイントはサーバ側で例外が起きても rescue により HTTP 200 のまま
        // {"status":false,"data":[],"message":"..."} を返すことがある。
        // 正常データとして呼び出し元へ渡すと参照エラーになるため、エラー扱いにして
        // SWR の error 経由（noticeError → null 返却）に乗せる。
        const responseBody: unknown = res.data;
        if (
          typeof responseBody === "object" &&
          responseBody !== null &&
          "status" in responseBody &&
          responseBody.status === false
        ) {
          const message = "message" in responseBody ? String(responseBody.message) : "";
          const envelopeError = new Error(`Ajax error response (status: false): ${url} ${message}`);
          envelopeError.name = AJAX_ENVELOPE_ERROR_NAME;
          throw envelopeError;
        }
        return res.data;
      }),
    {
      // status:false（rescue 由来）や HTTP 4xx は決定的に失敗し続けるため再試行しない。
      // 一時的なネットワークエラーや HTTP 5xx は再試行するが、SWR デフォルトの
      // 無制限リトライだと失敗中のエンドポイントへ POST が繰り返されるため上限を設ける
      shouldRetryOnError: (err: Error) => !isDeterministicError(err),
      errorRetryCount: 3,
      // レンダーのたびに重複通知しないよう、リクエスト失敗ごとに1回だけ New Relic へ通知する
      onError: (err) => {
        if (typeof newrelic !== "undefined") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          newrelic.noticeError(err);
        }
      },
    },
  );

  if (error) return null;
  if (!data) return null;

  return data;
};
